import { useCallback, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { validateString } from 'src/utils/string/stringUtil';
import {
	deleteEQM3020_R01, downloadExcel_EQM3020_R01, getEQM3020_R01AllCount, getEQM3020_R01List,
	patchEQM3020_R01, postEQM3020_R01,
} from './Api_EQM3020_R01';
import { EQM3020_R01Dto } from './Dto_EQM3020_R01';
import { defaultEQM3020_R01Filter, EQM3020_R01Filter, useEQM3020_R01Store } from './Store_EQM3020_R01';

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM3020_R01Dto> | null>;
	setMessage: (message: string) => void;
}

/** 조회·저장 후 토스트/메시지 표시 여부 */
type FetchMessageMode = 'show' | 'none';
interface FetchOptions { messageMode?: FetchMessageMode; }

/**
 * EQM3020 비즈니스 로직 (조회·등록·저장·삭제)
 * Actionbar 이벤트 핸들러와 Table에 필요한 상태를 반환
 */
export const Content_EQM3020_R01 = ({ setMessage }: Props) => {
	const {
		EQM3020_R01Filter,
		EQM3020_R01PastFilter, setEQM3020_R01PastFilter,
		EQM3020_R01List, setEQM3020_R01List,
		deletedEQM3020_R01List, removeDeletedEQM3020_R01List, addDeletedEQM3020_R01List,
		selectedEQM3020_R01List, setSelectedEQM3020_R01List, removeSelectedEQM3020_R01List,
		setOriginalEQM3020_R01List,
		setAllCount, setScrollTop,
	} = useEQM3020_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	/** 그리드 행 클릭 선택 허용 여부 (순번 영역 클릭 직후 1회 차단용) */
	const [clickable, setClickable] = useState(true);

	useFetchActionbarAuth('EQM3020_R01');

	/** API 조회 파라미터 변환 (빈 문자열 필드 제외) */
	const getParams = (f: EQM3020_R01Filter): Partial<EQM3020_R01Filter> => ({
		year: f.year,
		equipmentCd: f.equipmentCd || undefined,
		equipmentNm: f.equipmentNm || undefined,
	});

	/** 이전 조회 조건과 현재 조건이 동일한지 비교 */
	const isSameFilter = (a: EQM3020_R01Filter, b: EQM3020_R01Filter) =>
		a.year === b.year && a.equipmentCd === b.equipmentCd && a.equipmentNm === b.equipmentNm;

	/** 전체 건수 API 호출 후 스토어 갱신 */
	const renewalAllCount = useCallback(async () => {
		const res = await getEQM3020_R01AllCount(getParams(EQM3020_R01Filter));
		setAllCount(res.data.data);
		return res.data.data;
	}, [EQM3020_R01Filter, setAllCount]);

	/**
	 * 저장 전 행 유효성 검사
	 * - 계측기·교정일자 필수
	 * - 동일 계측기+교정일자 중복 불가
	 */
	const validateRow = (data: EQM3020_R01Dto, currentRowIndex: number) => {
		if (!validateString(data.equipmentCd)) { showWarning('계측기 정보가 없습니다.'); return false; }
		if (!validateString(data.calibDt))     { showWarning('교정일자를 입력해주세요.'); return false; }

		const activeRows = EQM3020_R01List.filter(
			(row) => !deletedEQM3020_R01List.includes(row.valueIndex.toString()));
		const others = activeRows.filter((_, i) => i !== currentRowIndex);
		if (others.some((r) => r.equipmentCd === data.equipmentCd && r.calibDt === data.calibDt)) {
			showWarning(`동일 계측기·교정일자가 중복됩니다. (${data.equipmentCd} / ${data.calibDt})`);
			return false;
		}
		return true;
	};

	/**
	 * 조회·재조회 공통 처리
	 * - search: Actionbar 조회 버튼
	 * - refetch: 저장 후 목록 갱신 (messageMode: 'none'으로 토스트 생략 가능)
	 */
	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions,
	) => {
		/** 조회 완료 메시지 표시 여부 ('show' | 'none') */
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		/** 현재 검색조건과 이전 조회 조건 동일 여부 */
		const same = isSameFilter(EQM3020_R01Filter, EQM3020_R01PastFilter);

		if (!same || fetchType === 'refetch') setScrollTop(null);
		if (fetchType === 'refetch' || !same) setEQM3020_R01List([]);

		/** 계측기 검교정 결과 목록 API 응답 */
		const res = await getEQM3020_R01List('search', getParams(EQM3020_R01Filter), EQM3020_R01List.length ?? 0);
		/** 그리드에 바인딩할 조회 결과 목록 */
		const newList = [...res.data.data] as EQM3020_R01Dto[];
		setEQM3020_R01List(newList);

		/** 전체 건수 (페이징·조회 완료 메시지용) */
		const processAllCount = await renewalAllCount();
		setOriginalEQM3020_R01List(newList);
		setSelectedEQM3020_R01List([]);
		removeDeletedEQM3020_R01List(deletedEQM3020_R01List);

		if (newList.length === 0) {
			if (messageMode === 'show') showWarning('조회된 계측기가 없습니다.');
		} else {
			setEQM3020_R01PastFilter(EQM3020_R01Filter);
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			}
		}

		setLoadingSpinner(false);
		if (messageMode === 'show') setTimeout(() => setMessage(''), 5000);
	}, [
		EQM3020_R01Filter, EQM3020_R01PastFilter, EQM3020_R01List, deletedEQM3020_R01List,
		setMessage, setLoadingSpinner, setOriginalEQM3020_R01List, setSelectedEQM3020_R01List,
		setEQM3020_R01List, removeDeletedEQM3020_R01List, renewalAllCount, setEQM3020_R01PastFilter, setScrollTop,
	]);

	/** Actionbar 조회 버튼 핸들러 */
	const fetchData = useCallback(async () => { await executeDataFetch('search'); }, [executeDataFetch]);

	/** Actionbar Excel 버튼 핸들러 */
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const params: Partial<EQM3020_R01Filter> = {
				year: EQM3020_R01Filter.year,
				equipmentCd: EQM3020_R01Filter.equipmentCd || undefined,
				equipmentNm: EQM3020_R01Filter.equipmentNm || undefined,
			};
			const response = await downloadExcel_EQM3020_R01(params);
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: '검교정 결과 등록.xlsx',
			});
			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error) {
			console.error('검교정 결과 엑셀 다운로드 실패:', error);
			showWarning('엑셀 다운로드에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [EQM3020_R01Filter, setLoadingSpinner]);

	/**
	 * Actionbar 등록 버튼 핸들러
	 * - 행 추가 없이 조회된 전 행의 입력 컬럼만 비우고 inserted=true
	 * - 계측기 정보(코드·명·교정예정일)는 유지
	 */
	const handleInsertClick = useCallback(() => {
		if (EQM3020_R01List.length === 0) {
			showWarning('먼저 조회해주세요.');
			return;
		}

		const clearedList = EQM3020_R01List.map((row) => ({
			...row,
			calibDt: '',
			empNo: '',
			empNm: '',
			passYn: '',
			nextCalibDt: '',
			resultFileId: '',
			resultFileNm: '',
			resultFileData: '',
			remark: '',
			inserted: true,
			isModified: false,
		}));

		setEQM3020_R01List(clearedList);
		removeSelectedEQM3020_R01List();
		setEQM3020_R01PastFilter(defaultEQM3020_R01Filter);
	}, [EQM3020_R01List, setEQM3020_R01List, removeSelectedEQM3020_R01List, setEQM3020_R01PastFilter]);

	/**
	 * Actionbar 저장 버튼 핸들러
	 * - 교정일자가 있는 inserted → POST
	 * - isModified → PATCH
	 * - deleted 표시 행 → DELETE (건당 API)
	 * - 완료 후 refetch
	 */
	const handleSaveClick = useCallback(async () => {
		setLoadingSpinner(true);
		/** 등록 모드(inserted)이면서 교정일자가 입력된 신규 저장 대상 */
		const insertedList = EQM3020_R01List.filter(
			(row) =>
				!deletedEQM3020_R01List.includes(row.valueIndex.toString()) &&
				row.inserted === true &&
				validateString(row.calibDt));
		/** 기존 데이터 중 수정(isModified)되고 교정일자가 있는 저장 대상 */
		const modifiedList = EQM3020_R01List.filter(
			(row) =>
				!deletedEQM3020_R01List.includes(row.valueIndex.toString()) &&
				row.isModified && !row.inserted &&
				validateString(row.calibDt));

		if (insertedList.length === 0 && modifiedList.length === 0 && deletedEQM3020_R01List.length === 0) {
			showWarning('저장할 검교정 결과가 없습니다. (교정일자를 입력해주세요)');
			setLoadingSpinner(false);
			return;
		}

		/** validateRow 통과 여부 (중복·필수값 검사) */
		let valid = true;
		modifiedList.forEach((value) => {
			/** 그리드 행 인덱스 (validateRow 에러 포커스용) */
			const index = EQM3020_R01List.findIndex((row) => row.valueIndex === value.valueIndex);
			if (!validateRow(value, index)) valid = false;
		});
		if (!valid) {
			setLoadingSpinner(false);
			return;
		}
		insertedList.forEach((value) => {
			/** 그리드 행 인덱스 (validateRow 에러 포커스용) */
			const index = EQM3020_R01List.findIndex((row) => row.valueIndex === value.valueIndex);
			if (!validateRow(value, index)) valid = false;
		});
		if (!valid) {
			setLoadingSpinner(false);
			return;
		}

		/** 삭제 표시된 valueIndex 문자열 목록 */
		const deleteStringList = deletedEQM3020_R01List.map((i) => i.toString());
		/** 삭제 API에 넘길 실제 행 DTO 목록 */
		const deleteTargetList = EQM3020_R01List.filter(
			(i) => deleteStringList.includes(i.valueIndex.toString()));

		/** insRes: POST 응답, modRes: PATCH 응답, delRes: DELETE 응답(복수 건 Promise.all) */
		const [insRes, modRes, delRes] = await Promise.all([
			insertedList.length > 0 ? postEQM3020_R01(insertedList) : Promise.resolve(undefined),
			modifiedList.length > 0 ? patchEQM3020_R01(modifiedList) : Promise.resolve(undefined),
			deletedEQM3020_R01List.length > 0
				? Promise.all(deleteTargetList.map((v) => deleteEQM3020_R01(v)))
				: Promise.resolve(undefined),
		]);

		if (insRes?.data.successFlag || modRes?.data.successFlag) {
			showSuccess('검교정 결과 저장이 완료되었습니다.');
			setMessage('검교정 결과 저장이 완료되었습니다.');
		} else if (delRes) {
			showSuccess('검교정 결과 삭제가 완료되었습니다.');
			setMessage('검교정 결과 삭제가 완료되었습니다.');
		}
		setTimeout(() => setMessage(''), 5000);
		await executeDataFetch('refetch', { messageMode: 'none' });
		setLoadingSpinner(false);
	}, [EQM3020_R01List, deletedEQM3020_R01List, executeDataFetch, setMessage]);

	/**
	 * Actionbar 삭제 버튼 핸들러 (소프트 삭제)
	 * - 삭제 표시된 행이 있으면 → 표시 취소(되돌리기)
	 * - 선택 행만 있으면 → deletedEQM3020_R01List에 추가 (저장 시 API 삭제)
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedEQM3020_R01List.length > 0) {
			removeDeletedEQM3020_R01List(deletedEQM3020_R01List);
			removeSelectedEQM3020_R01List();
			setEQM3020_R01List([...EQM3020_R01List]);
		} else if (selectedEQM3020_R01List.length > 0) {
			addDeletedEQM3020_R01List(selectedEQM3020_R01List);
			removeSelectedEQM3020_R01List(selectedEQM3020_R01List);
		}
	}, [
		selectedEQM3020_R01List, deletedEQM3020_R01List,
		addDeletedEQM3020_R01List, removeDeletedEQM3020_R01List,
		removeSelectedEQM3020_R01List, setEQM3020_R01List, EQM3020_R01List,
	]);

	return {
		clickable, setClickable,
		fetchData,
		handleExcelClick,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
	};
};
